import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchLowStockPathItems } from "../services/pathLowStockService";
import {
  canShowPathLowStockAlert,
  hasShownPathLowStockAlertThisSession,
  markPathLowStockAlertShown,
  readPathLowStockQuantityThreshold,
} from "../utils/pathLowStock";
import {
  resolveOrganizationId,
  waitForOrganizationId,
} from "../utils/resolveOrganizationId";
import { isLoginValidationInProgress } from "../utils/loginSession";
import PathLowStockAlertDialog from "./PathLowStockAlertDialog";
import { useSubscriptionCapabilities } from "../hooks/useSubscriptionCapabilities";

const PathLowStockAlertHandler = () => {
  const { isAuthenticated, user } = useAuth();
  const { hasPathPackageAccess, loading: subscriptionLoading } =
    useSubscriptionCapabilities();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const alertCompletedRef = useRef(false);

  const canShow =
    !subscriptionLoading &&
    hasPathPackageAccess &&
    canShowPathLowStockAlert(user?.roleName);

  useEffect(() => {
    if (!isAuthenticated || isLoginValidationInProgress()) return;
    if (!canShow) return;
    if (hasShownPathLowStockAlertThisSession(user?.id)) return;
    if (alertCompletedRef.current) return;

    let cancelled = false;

    const runAlertFetch = async () => {
      let organizationId = resolveOrganizationId(user);
      if (!organizationId) {
        organizationId = await waitForOrganizationId(user);
      }
      if (!organizationId || cancelled) {
        return false;
      }

      try {
        const list = await fetchLowStockPathItems(organizationId);
        if (!cancelled && list.length > 0) {
          setItems(list);
          setOpen(true);
        }
      } catch (error) {
        console.warn("[PathLowStockAlert] Unable to load low stock list:", error);
      } finally {
        if (!cancelled) {
          alertCompletedRef.current = true;
          markPathLowStockAlertShown(user?.id);
        }
      }
      return true;
    };

    runAlertFetch();

    const onOrgUpdated = () => {
      if (
        cancelled ||
        alertCompletedRef.current ||
        hasShownPathLowStockAlertThisSession(user?.id)
      ) {
        return;
      }
      runAlertFetch();
    };

    window.addEventListener("userOrganizationIdUpdated", onOrgUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener("userOrganizationIdUpdated", onOrgUpdated);
    };
  }, [
    isAuthenticated,
    user,
    user?.id,
    user?.roleName,
    user?.organizationId,
    canShow,
  ]);

  const handleClose = () => setOpen(false);

  const handleGoToInventory = () => {
    setOpen(false);
    navigate("/inventory");
  };

  if (!isAuthenticated || !canShow) {
    return null;
  }

  return (
    <PathLowStockAlertDialog
      open={open}
      onClose={handleClose}
      items={items}
      quantityThreshold={readPathLowStockQuantityThreshold(resolveOrganizationId(user))}
      onGoToInventory={handleGoToInventory}
    />
  );
};

export default PathLowStockAlertHandler;
